import { EventDropArg } from '@fullcalendar/core'
import { FoodBookingModel, isBooking, ServiceBookingModel } from '@xeetr/types/models'
import { isEqual } from 'date-fns'
import { useSetAtom } from 'jotai'
import { useContext } from 'react'

import { moveBookingDialogAtom } from '../../atoms/calendar'
import { ActiveTenantContext } from '../../context/common/Context'
import { prepareBooking } from '../../utils/calendar'
import { useCalendar } from '../useCalendar'

import { useBookings } from '@/hooks/useBookings'
import { showToast } from '@/utils/toastMessage'
import { isFoodBookingModel } from '@xeetr/types/utils'

interface ReturnType {
  moveBooking: (argument: EventDropArg) => void
}

export const useMoveBooking = (): ReturnType => {
  const { events, refetchResources } = useCalendar()
  const setState = useSetAtom(moveBookingDialogAtom)
  const { update } = useBookings()
  const { tenant } = useContext(ActiveTenantContext)

  const moveBooking = (argument: EventDropArg) => {
    const event = events?.find((e) => e.id === argument.event._def.publicId)
    if (!event || !isBooking(event)) {
      refetchResources()
      showToast({
        severity: 'info',
        detail: 'Nur Reservierungen können verschoben werden.',
      })
      return
    }

    const booking = event.data as FoodBookingModel | ServiceBookingModel
    if (!booking || !tenant) {
      refetchResources()
      showToast({
        severity: 'info',
        detail:
          'Events können nicht als Reservierung eingetragen werden. Bitte erstellen Sie eine separate Reservierung.',
      })
      return
    }

    const newDate = argument.event._instance?.range.start
    const oldDate = argument.oldEvent._instance?.range.start
    const movedInTime = !!oldDate && !!newDate && !isEqual(oldDate, newDate)
    const movedInSpace = argument.oldResource?._resource.id !== argument.newResource?._resource.id

    // dropping into the calendar "event" column is not allowed
    if (movedInSpace && argument.newResource?._resource.id === 'event') {
      refetchResources()
      showToast({
        severity: 'info',
        detail:
          'Reservierungen können nicht als Event eingetragen werden. Bitte erstellen Sie ein separates Event.',
      })
      argument.revert()
      return
    }

    // remember how many tables the booking had before anything is changed
    const originalTableCount = isFoodBookingModel(booking) ? (booking.tables?.length ?? 0) : 0

    let newBooking: FoodBookingModel | ServiceBookingModel = { ...booking }

    if (movedInSpace) newBooking = prepareBooking(newBooking, tenant.type, argument)

    if (movedInSpace && !movedInTime) {
      const newResourceId = argument.newResource?._resource.id
      if (isFoodBookingModel(booking) && isFoodBookingModel(newBooking)) {
        // 0. booking had no tables before, now it's being assigned to one – ask first
        const nowHasTable = newBooking.tables && newBooking.tables.length > 0
        if (originalTableCount === 0 && nowHasTable) {
          setState({
            open: true,
            kind: 'assignTable',
            booking: booking,
            onConfirm: () => {
              update(newBooking, false)
            },
            onCancel: () => {
              argument.revert()
            },
          })
          return
        }

        // 0.5 booking moved into a different room – old tables become invalid
        const oldRoomId =
          argument.oldResource?._resource.parentId || argument.oldResource?._resource.id
        const newRoomId =
          argument.newResource?._resource.parentId || argument.newResource?._resource.id
        if (
          oldRoomId &&
          newRoomId &&
          oldRoomId !== newRoomId &&
          booking.tables &&
          booking.tables.length > 0
        ) {
          // keep the newly assigned table if applicable
          if (argument.newResource?._resource.parentId) {
            newBooking.tables = newBooking.tables.filter(
              (t) => t.id === newResourceId
            )
          } else {
            newBooking.tables = []
          }
          setState({
            open: true,
            kind: 'newRoom',
            booking: newBooking,
            onConfirm: () => {
              update(newBooking, false)
            },
            onCancel: () => {
              argument.revert()
            },
          })
          return
        }

        // 1. moving between two tables that already belong to the same booking
        //    (would result in a duplicate table entry)
        const hasAlready = booking.tables && booking.tables.some((t) => t.id === newResourceId)
        if (hasAlready) {
          showToast({
            severity: 'info',
            detail: 'Dieser Tisch ist bereits der Reservierung zugewiesen.',
          })
          argument.revert()
          return
        }

        // 2. dragging into a room/blank space clears all tables – ask before saving
        const isRoomDrop = !argument.newResource?._resource.parentId
        if (isRoomDrop && booking.tables && booking.tables.length > 0) {
          newBooking.tables = []
          setState({
            open: true,
            kind: 'clearTables',
            booking: newBooking,
            onConfirm: () => {
              update(newBooking, false)
            },
            onCancel: () => {
              argument.revert()
            },
          })
          return
        }
      }

      // otherwise the move is harmless – persist immediately
      update(newBooking, false)
      return
    }

    if (movedInTime) {
      const timeDifference = argument.delta.milliseconds
      setState({
        open: true,
        booking: newBooking,
        timeDifference,
        kind: 'time',
        onCancel: () => argument.revert(),
      })
    } else {
      update(newBooking, false)
    }
  }
  return { moveBooking }
}
